import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LOGO_MONOGRAMA } from "@/components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal. Intenta nuevamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const FIELDS = [
  ["matches", "Partidos jugados", "admin-input-matches"],
  ["starts", "Titularidades", "admin-input-starts"],
  ["minutes", "Minutos jugados", "admin-input-minutes"],
  ["goals", "Goles", "admin-input-goals"],
  ["assists", "Asistencias", "admin-input-assists"],
  ["yellow_cards", "Tarjetas amarillas", "admin-input-yellow-cards"],
];

export default function Admin() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true }).then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/stats`, { withCredentials: true }).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/contact`, { withCredentials: true }).then((r) => setMessages(r.data)).catch(() => {});
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...stats, matches: +stats.matches, starts: +stats.starts, minutes: +stats.minutes, goals: +stats.goals, assists: +stats.assists, yellow_cards: +stats.yellow_cards };
      const { data } = await axios.put(`${API}/stats`, payload, { withCredentials: true });
      setStats(data);
      toast.success("Estadísticas actualizadas en el sitio");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (user === null) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center font-tech text-neutral-500 text-xs uppercase tracking-[0.3em]">Cargando…</div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm border border-neutral-800 bg-[#0f0f0f] p-8">
          <div className="flex justify-center mb-6"><img src={LOGO_MONOGRAMA} alt="Monograma CD de Cristian Díaz" className="h-16 w-auto" /></div>
          <h1 className="font-display text-2xl font-extrabold uppercase text-center mb-1">Panel del jugador</h1>
          <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-neutral-500 text-center mb-8">Acceso representación</p>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="admin-email-input"
            className="w-full bg-[#050505] border border-neutral-800 px-4 py-3 text-sm mb-3 placeholder:text-neutral-600 focus:border-[#d4af37] focus:outline-none transition-colors" />
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Contraseña" data-testid="admin-password-input"
            className="w-full bg-[#050505] border border-neutral-800 px-4 py-3 text-sm mb-3 placeholder:text-neutral-600 focus:border-[#d4af37] focus:outline-none transition-colors" />
          {loginError && <p data-testid="admin-login-error" className="text-red-400 text-xs font-tech mb-3">{loginError}</p>}
          <button type="submit" data-testid="admin-login-button"
            className="w-full font-tech text-xs uppercase tracking-[0.25em] bg-white text-black px-8 py-4 hover:bg-[#d4af37] transition-colors">
            Entrar →
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] px-6 py-10" data-testid="admin-dashboard">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <img src={LOGO_MONOGRAMA} alt="Monograma CD de Cristian Díaz" className="h-9 w-auto" />
            <div>
              <h1 className="font-display text-xl font-extrabold uppercase leading-none">Panel de administración</h1>
              <p className="font-tech text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">{user.email}</p>
            </div>
          </div>
          <button onClick={logout} data-testid="admin-logout-button"
            className="font-tech text-[10px] uppercase tracking-[0.25em] border border-neutral-700 px-4 py-2 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors">
            Salir
          </button>
        </div>

        {stats && (
          <form onSubmit={save} data-testid="admin-stats-form" className="border border-neutral-800 bg-[#0f0f0f] p-8 mb-8">
            <h2 className="font-display text-2xl font-extrabold uppercase mb-6">Estadísticas en el sitio</h2>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <label className="block">
                <span className="font-tech text-[10px] uppercase tracking-[0.25em] text-neutral-500">Competencia</span>
                <input value={stats.competition_label} onChange={(e) => setStats({ ...stats, competition_label: e.target.value })} data-testid="admin-input-competition"
                  className="mt-1 w-full bg-[#050505] border border-neutral-800 px-4 py-3 text-sm focus:border-[#d4af37] focus:outline-none transition-colors" />
              </label>
              <label className="block">
                <span className="font-tech text-[10px] uppercase tracking-[0.25em] text-neutral-500">Fecha de corte</span>
                <input value={stats.updated_label} onChange={(e) => setStats({ ...stats, updated_label: e.target.value })} data-testid="admin-input-updated"
                  className="mt-1 w-full bg-[#050505] border border-neutral-800 px-4 py-3 text-sm focus:border-[#d4af37] focus:outline-none transition-colors" />
              </label>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
              {FIELDS.map(([key, label, id]) => (
                <label key={key} className="block">
                  <span className="font-tech text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label}</span>
                  <input type="number" min="0" value={stats[key]} onChange={(e) => setStats({ ...stats, [key]: e.target.value })} data-testid={id}
                    className="mt-1 w-full bg-[#050505] border border-neutral-800 px-4 py-3 text-sm focus:border-[#d4af37] focus:outline-none transition-colors" />
                </label>
              ))}
            </div>
            <button type="submit" disabled={saving} data-testid="admin-save-button"
              className="font-tech text-xs uppercase tracking-[0.25em] bg-white text-black px-8 py-4 hover:bg-[#d4af37] transition-colors disabled:opacity-50">
              {saving ? "Guardando…" : "Guardar y publicar →"}
            </button>
          </form>
        )}

        <div className="border border-neutral-800 bg-[#0f0f0f] p-8" data-testid="admin-messages">
          <h2 className="font-display text-2xl font-extrabold uppercase mb-6">Mensajes recibidos <span className="text-[#d4af37]">({messages.length})</span></h2>
          {messages.length === 0 && <p className="text-sm text-neutral-500">Aún no llegan mensajes desde el formulario.</p>}
          <ul className="space-y-4">
            {messages.map((m) => (
              <li key={m.id} className="border border-neutral-800 p-4" data-testid={`admin-message-${m.id}`}>
                <div className="flex flex-wrap justify-between gap-2 mb-2">
                  <p className="font-display font-bold uppercase">{m.name} {m.organization && <span className="text-neutral-500">· {m.organization}</span>}</p>
                  <p className="font-tech text-[10px] text-neutral-500">{new Date(m.timestamp).toLocaleString("es-CL")}</p>
                </div>
                <p className="text-sm text-neutral-400">{m.message}</p>
                <a href={`mailto:${m.email}`} className="font-tech text-xs text-[#d4af37] mt-2 inline-block hover:underline">{m.email}</a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
